import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MuiLoadingButton from './MuiLoadingButton';

/**
 * MuiConfirmDialog - Confirmation dialog for destructive actions
 *
 * Von Restorff Effect: Error color for destructive actions stands out
 * Hick's Law: Only two choices - confirm or cancel
 * Jakob's Law: Standard confirmation dialog pattern
 */

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 12,
    minWidth: 400,
    maxWidth: 480,
  },
}));

const IconWrapper = styled(Box)(({ theme, severity }) => {
  const colorMap = {
    error: {
      bg: `${theme.palette.error.main}14`,
      color: theme.palette.error.main,
    },
    warning: {
      bg: `${theme.palette.warning.main}14`,
      color: theme.palette.warning.main,
    },
    info: {
      bg: `${theme.palette.info.main}14`,
      color: theme.palette.info.main,
    },
  };

  const colors = colorMap[severity] || colorMap.warning;

  return {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: colors.bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    '& .MuiSvgIcon-root': {
      fontSize: 28,
      color: colors.color,
    },
  };
});

const severityIcons = {
  error: DeleteOutlineIcon,
  warning: WarningAmberIcon,
  info: HelpOutlineIcon,
  danger: ErrorOutlineIcon,
};

const MuiConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'warning',
  confirmColor,
  loading = false,
  icon,
  showIcon = true,
  showCloseButton = true,
  disableBackdropClick = true,
  itemName,
  children,
  sx = {},
  ...props
}) => {
  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    onClose?.(event, reason);
  };

  const handleConfirm = async (event) => {
    await onConfirm?.(event);
  };

  const IconComponent = icon || severityIcons[severity] || WarningAmberIcon;
  const buttonColor = confirmColor || (severity === 'error' || severity === 'danger' ? 'error' : 'primary');

  // Build message with item name if provided
  const displayMessage = itemName
    ? (typeof message === 'string'
        ? message.replace('{itemName}', itemName)
        : message)
    : message;

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      sx={sx}
      {...props}
    >
      <DialogTitle sx={{ p: 0 }}>
        {showCloseButton && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            disabled={loading}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        {showIcon && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <IconWrapper severity={severity}>
              <IconComponent />
            </IconWrapper>
          </Box>
        )}
        <Typography
          variant="h6"
          component="h2"
          fontWeight={600}
          gutterBottom
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 320, mx: 'auto' }}
        >
          {displayMessage}
        </Typography>
        {itemName && (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}
          >
            {itemName}
          </Typography>
        )}
        {children}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, gap: 1.5 }}>
        <MuiLoadingButton
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={loading}
          fullWidth
          sx={{
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'text.secondary',
              backgroundColor: 'action.hover',
            },
          }}
        >
          {cancelText}
        </MuiLoadingButton>
        <MuiLoadingButton
          variant="contained"
          color={buttonColor}
          onClick={handleConfirm}
          loading={loading}
          fullWidth
        >
          {confirmText}
        </MuiLoadingButton>
      </DialogActions>
    </StyledDialog>
  );
};

// Preset configurations for common use cases
MuiConfirmDialog.Delete = (props) => (
  <MuiConfirmDialog
    severity="error"
    title="Delete Item"
    message="Are you sure you want to delete this item? This action cannot be undone."
    confirmText="Delete"
    confirmColor="error"
    {...props}
  />
);

MuiConfirmDialog.Discard = (props) => (
  <MuiConfirmDialog
    severity="warning"
    title="Discard Changes"
    message="You have unsaved changes. Are you sure you want to discard them?"
    confirmText="Discard"
    confirmColor="warning"
    {...props}
  />
);

MuiConfirmDialog.Logout = (props) => (
  <MuiConfirmDialog
    severity="info"
    title="Log Out"
    message="Are you sure you want to log out of your account?"
    confirmText="Log Out"
    confirmColor="primary"
    {...props}
  />
);

MuiConfirmDialog.propTypes = {
  /** Controls dialog visibility */
  open: PropTypes.bool.isRequired,
  /** Callback when dialog should close */
  onClose: PropTypes.func,
  /** Callback when action is confirmed */
  onConfirm: PropTypes.func,
  /** Dialog title */
  title: PropTypes.node,
  /** Dialog message */
  message: PropTypes.node,
  /** Confirm button text */
  confirmText: PropTypes.string,
  /** Cancel button text */
  cancelText: PropTypes.string,
  /** Severity affects icon and default colors */
  severity: PropTypes.oneOf(['error', 'warning', 'info', 'danger']),
  /** Override confirm button color */
  confirmColor: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error']),
  /** Loading state for confirm button */
  loading: PropTypes.bool,
  /** Custom icon component */
  icon: PropTypes.elementType,
  /** Show severity icon */
  showIcon: PropTypes.bool,
  /** Show close button */
  showCloseButton: PropTypes.bool,
  /** Disable closing by clicking backdrop */
  disableBackdropClick: PropTypes.bool,
  /** Item name to display (replaces {itemName} in message) */
  itemName: PropTypes.string,
  /** Additional content */
  children: PropTypes.node,
  /** Custom styles */
  sx: PropTypes.object,
};

export default MuiConfirmDialog;
